// components/CameraStream.tsx
import React, { useEffect, useRef, useState, useImperativeHandle, forwardRef } from "react";
import videojs from "video.js";
import "video.js/dist/video-js.css";
import { usePlayback } from "../../../context/VideoPlayback";

const CameraStream = forwardRef(({ url, webrtcUrl, hlsUrl, isPlaying, mode }, ref) => {
  const videoNode = useRef(null);
  const player = useRef(null);
  const webrtcVideoRef = useRef(null);
  const peerConnectionRef = useRef(null);
  const webrtcTimeoutRef = useRef(null);
  const webrtcConnectingRef = useRef(false); // Prevent multiple simultaneous connection attempts
  const { currentTime } = usePlayback();
  const [isWebRTC, setIsWebRTC] = useState(false);
  const [streamLoading, setStreamLoading] = useState(false);
  const [webrtcFailed, setWebrtcFailed] = useState(false);

  // Track previous webrtcUrl to detect when it changes (new stream)
  const prevWebrtcUrlRef = useRef(webrtcUrl);

  // Reset webrtcFailed when webrtcUrl changes (new stream)
  useEffect(() => {
    if (prevWebrtcUrlRef.current !== webrtcUrl) {
      setWebrtcFailed(false);
      prevWebrtcUrlRef.current = webrtcUrl;
    }
  }, [webrtcUrl]);

  // Detect if URL is WebRTC and determine if we should try WebRTC first
  useEffect(() => {
    if (webrtcUrl && !webrtcFailed) {
      // We have a webrtc URL and haven't failed yet, try WebRTC
      setIsWebRTC(true);
    } else if (webrtcFailed && hlsUrl) {
      // WebRTC failed, use HLS
      setIsWebRTC(false);
    } else if (hlsUrl && !webrtcUrl) {
      // Only HLS available, no WebRTC
      setIsWebRTC(false);
    } else if (url && !webrtcUrl && !hlsUrl) {
      // Fallback to using the url prop directly
      const isWebRTCUrl = url.includes("webrtc://") || url.includes(":8889");
      setIsWebRTC(isWebRTCUrl);
    }
  }, [url, webrtcUrl, hlsUrl, webrtcFailed]);

  // WebRTC setup with signaling
  useEffect(() => {
    // Prevent multiple simultaneous connection attempts
    if (webrtcConnectingRef.current) {
      return;
    }

    // If WebRTC already failed and we have HLS, don't try WebRTC again
    if (webrtcFailed && hlsUrl) {
      return;
    }

    const webrtcUrlToUse = webrtcUrl || url;
    if (isWebRTC && webrtcVideoRef.current && webrtcUrlToUse && !webrtcFailed) {
      webrtcConnectingRef.current = true;
      setStreamLoading(true);

      const video = webrtcVideoRef.current;
      const pc = new RTCPeerConnection({
        iceServers: [{ urls: "stun:stun.l.google.com:19302" }],
      });

      // Set up video track handler
      pc.ontrack = (event) => {
        if (video && event.streams && event.streams[0]) {
          // Clear timeout on successful track reception
          if (webrtcTimeoutRef.current) {
            clearTimeout(webrtcTimeoutRef.current);
            webrtcTimeoutRef.current = null;
          }

          video.srcObject = event.streams[0];
          setStreamLoading(false);
          if (isPlaying) {
            video.play().catch((error) => {
              console.warn("WebRTC play interrupted: ", error);
            });
          }
        }
      };

      // Handle connection state changes
      pc.onconnectionstatechange = () => {
        console.log("WebRTC connection state:", pc.connectionState);
        if (
          pc.connectionState === "failed" ||
          pc.connectionState === "disconnected"
        ) {
          setStreamLoading(false);
          // If WebRTC fails and we have HLS, fallback to HLS
          if (hlsUrl && !webrtcFailed) {
            console.log("WebRTC failed, falling back to HLS");
            setWebrtcFailed(true);
            webrtcConnectingRef.current = false;
            if (pc) {
              pc.close();
            }
          }
        }
      };

      // Convert HTTP webrtc URL to webrtc:// format for signaling
      // Note: WebRTC requires signaling - we can't use the URL directly in the video element
      // The signaling server needs the stream URL to negotiate the connection
      const convertToWebRTCUrl = (httpUrl) => {
        try {
          const urlObj = new URL(httpUrl);
          // Extract the path (e.g., /live/869492052755393/1/)
          const path = urlObj.pathname.replace(/\/$/, ""); // Remove trailing slash
          // Convert to webrtc:// format (required by signaling server)
          return `webrtc://${urlObj.hostname}${path}`;
        } catch (error) {
          console.error("Error converting WebRTC URL:", error);
          return null;
        }
      };

      // Start WebRTC connection
      // This is the correct WebRTC flow:
      // 1. Create peer connection and offer
      // 2. Send offer + stream URL to signaling server
      // 3. Receive answer from signaling server
      // 4. Video stream comes through RTCPeerConnection (not directly from URL)
      const startWebRTC = async () => {
        // Set a timeout for WebRTC connection (15 seconds)
        // If it doesn't connect in time, fallback to HLS
        webrtcTimeoutRef.current = setTimeout(() => {
          if (hlsUrl && !webrtcFailed) {
            console.log("WebRTC connection timeout, falling back to HLS");
            setWebrtcFailed(true);
            webrtcConnectingRef.current = false;
            if (pc) {
              pc.close();
            }
          }
        }, 15000);

        try {
          // Step 1: Create WebRTC offer
          const offer = await pc.createOffer();
          await pc.setLocalDescription(offer);

          // Step 2: Convert the webrtc URL from API to format needed by signaling server
          // API gives us: http://192.168.159.11:8889/live/869492052755393/1/
          // Signaling server needs: webrtc://192.168.159.11/live/869492052755393/1
          const webrtcUrlToUse = webrtcUrl || url;
          const streamUrl = convertToWebRTCUrl(webrtcUrlToUse);
          if (!streamUrl) {
            throw new Error("Failed to convert WebRTC URL");
          }

          // Step 3: Send offer + stream URL to signaling server
          // The signaling server negotiates the connection and returns an answer
          const signalingApi = "http://145.223.116.87:1985/rtc/v1/play/";
          const requestBody = {
            api: signalingApi,
            streamurl: streamUrl,
            sdp: offer.sdp,
          };

          let response;
          try {
            response = await fetch(signalingApi, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(requestBody),
            });
          } catch (fetchError) {
            throw fetchError;
          }

          if (!response.ok) {
            const errorText = await response.text();
            throw new Error(
              `Signaling server error: ${response.status} - ${errorText}`,
            );
          }

          const data = await response.json();

          // Set remote description
          await pc.setRemoteDescription(
            new RTCSessionDescription({ type: "answer", sdp: data.sdp }),
          );

          // Clear timeout on success
          if (webrtcTimeoutRef.current) {
            clearTimeout(webrtcTimeoutRef.current);
            webrtcTimeoutRef.current = null;
          }

          peerConnectionRef.current = pc;
          webrtcConnectingRef.current = false;
        } catch (error) {

          console.error("WebRTC connection error:", error);

          // Clear timeout
          if (webrtcTimeoutRef.current) {
            clearTimeout(webrtcTimeoutRef.current);
            webrtcTimeoutRef.current = null;
          }

          // Fallback to HLS if available
          if (hlsUrl && !webrtcFailed) {
            console.log("WebRTC failed, falling back to HLS");
            setWebrtcFailed(true);
          } else {
            setStreamLoading(false);
          }

          if (pc) {
            pc.close();
          }
        }
      };

      startWebRTC();

      return () => {
        if (video) {
          video.pause();
          video.srcObject = null;
        }
        if (peerConnectionRef.current) {
          peerConnectionRef.current.close();
          peerConnectionRef.current = null;
        }
      };
    }
  }, [isWebRTC, url, isPlaying]);

  // HLS setup with video.js
  useEffect(() => {
    // Use HLS URL if available, otherwise fallback to url prop when WebRTC failed
    const hlsUrlToUse = hlsUrl || (webrtcFailed && !hlsUrl ? url : null);
    if (!isWebRTC && videoNode.current && hlsUrlToUse) {
      // Validate HLS URL format
      if (!hlsUrlToUse || (!hlsUrlToUse.startsWith('http://') && !hlsUrlToUse.startsWith('https://'))) {
        console.error("Invalid HLS URL format:", hlsUrlToUse);
        return;
      }

      // Normalize HLS URL - ensure it ends with / or has proper path
      let normalizedHlsUrl = hlsUrlToUse;
      if (!normalizedHlsUrl.endsWith('.m3u8') && !normalizedHlsUrl.endsWith('/')) {
        // If URL doesn't end with .m3u8 or /, try appending playlist.m3u8
        normalizedHlsUrl = normalizedHlsUrl.endsWith('/') 
          ? `${normalizedHlsUrl}playlist.m3u8` 
          : `${normalizedHlsUrl}/playlist.m3u8`;
      }

      // Add a small delay to ensure stream is ready before loading
      const initTimeout = setTimeout(() => {
        if (!videoNode.current) return;

        try {
          player.current = videojs(videoNode.current, {
            autoplay: isPlaying,
            controls: true,
            muted: true,
            preload: "auto",
            responsive: true,
            html5: {
              hls: {
                enableLowInitialPlaylist: true,
                smoothQualityChange: true,
                overrideNative: true,
              },
            },
            sources: [{ src: normalizedHlsUrl, type: "application/x-mpegURL" }],
            controlBar: {
              children: [
                "playToggle",
                "volumePanel",
                "currentTimeDisplay",
                "progressControl",
                "remainingTimeDisplay",
                "fullscreenToggle", // Keep it minimal to avoid overflow
              ],
            },
          });

          // Handle player errors
          player.current.on("error", (error) => {
            console.error("Video.js player error:", error);
            const playerError = player.current.error();
            if (playerError) {
              console.error("Player error details:", {
                code: playerError.code,
                message: playerError.message,
              });
            }
          });

          // Handle HLS errors specifically
          player.current.on("hlsError", (error, data) => {
            console.error("HLS error:", error, data);
            // If HLS fails, try the original URL without modification
            if (normalizedHlsUrl !== hlsUrlToUse && player.current && !player.current.isDisposed()) {
              console.log("Retrying with original HLS URL:", hlsUrlToUse);
              player.current.src({ src: hlsUrlToUse, type: "application/x-mpegURL" });
            }
          });

          player.current.ready(() => {
            if (isPlaying) {
              // Add a small delay to ensure stream is ready
              setTimeout(() => {
                if (player.current && !player.current.isDisposed()) {
                  player.current.play().catch((error) => {
                    console.warn("Play interrupted: ", error);
                  });
                }
              }, 1000);
            }
          });

          // Define Screenshot Button
          const Button = videojs.getComponent("Button");

      class ScreenshotButton extends Button {
        constructor(player, options) {
          super(player, options);
          this.controlText("Screenshot");
          this.addClass("vjs-screenshot-button"); // For custom CSS if needed
        }

        handleClick() {
          takeScreenshot();
        }
      }

      class CallButton extends Button {
        constructor(player, options) {
          super(player, options);
          this.controlText("Call");
          this.addClass("vjs-call-button"); // For custom CSS if needed
        }

        handleClick() {
          alert("Call functionality triggered!");
        }
      }

          // Register Components
          videojs.registerComponent("ScreenshotButton", ScreenshotButton);
          videojs.registerComponent("CallButton", CallButton);

          // Add Buttons to Control Bar
          player.current
            .getChild("controlBar")
            .addChild(
              "ScreenshotButton",
              {},
              player.current.controlBar.children().length - 1,
            );
          player.current
            .getChild("controlBar")
            .addChild(
              "CallButton",
              {},
              player.current.controlBar.children().length - 1,
            );
        } catch (initError) {
          console.error("Error initializing video.js player:", initError);
        }
      }, 2000); // 2 second delay to ensure stream is ready

      return () => {
        clearTimeout(initTimeout);
        if (player.current) {
          try {
            if (!player.current.isDisposed()) {
              player.current.pause();
              player.current.dispose();
            }
          } catch (disposeError) {
            console.error("Error disposing video.js player:", disposeError);
          }
          player.current = null;
        }
      };
    }

    return () => {
      if (player.current) {
        try {
          if (!player.current.isDisposed()) {
            player.current.pause();
          }
        } catch (pauseError) {
        }
        try {
          if (!player.current.isDisposed()) {
            player.current.dispose();
          } else {
          }
        } catch (disposeError) {
          console.error("Error disposing video.js player:", disposeError);
        }
        player.current = null;
      } else {
      }
    };
  }, [hlsUrl, url, webrtcFailed, isWebRTC]);

  useEffect(() => {
    if (isWebRTC && webrtcVideoRef.current) {
      const video = webrtcVideoRef.current;
      if (isPlaying) {
        video.play().catch((error) => {
          console.warn("WebRTC play interrupted: ", error);
        });
      } else {
        video.pause();
      }
    } else if (player.current) {
      if (isPlaying) {
        player.current.play().catch((error) => {
          console.warn("Play interrupted: ", error);
        });
      } else {
        player.current.pause();
      }
    }
  }, [isPlaying, isWebRTC]);

  useEffect(() => {
    if (mode === "playback" && player.current) {
      if (
        player.current.currentTime() &&
        Math.abs(player.current.currentTime() - currentTime) > 1
      ) {
        player.current.currentTime(currentTime);
      }
    }
  }, [currentTime, mode]);

  const takeScreenshot = (filename = "snapshot") => {
    const video = isWebRTC
      ? webrtcVideoRef.current
      : videoNode.current?.querySelector("video") || videoNode.current;
    if (!video) return;

    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
    const screenshotUrl = canvas.toDataURL("image/png");
    const link = document.createElement("a");
    link.href = screenshotUrl;
    link.download = `${filename}.png`;
    link.click();
  };

  useImperativeHandle(ref, () => ({ takeScreenshot }));

  if (isWebRTC) {
    return (
      <div style={{ width: "100%", height: "100%", position: "relative" }}>
        {streamLoading && (
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              color: "white",
              zIndex: 10,
            }}
          >
            Loading stream...
          </div>
        )}
        <video
          ref={webrtcVideoRef}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
          autoPlay
          playsInline
          controls
          muted
        />
        <button
          onClick={() => takeScreenshot()}
          style={{
            position: "absolute",
            bottom: "48px",
            right: "8px",
            background: "rgba(0,0,0,0.6)",
            border: "none",
            borderRadius: "4px",
            color: "white",
            cursor: "pointer",
            padding: "4px 8px",
            fontSize: "16px",
            zIndex: 20,
          }}
          title="Take Snapshot"
          type="button"
        >
          📷
        </button>
      </div>
    );
  }

  return (
    <div data-vjs-player>
      <video
        ref={videoNode}
        className="video-js vjs-default-skin"
        style={{ width: "100%", height: "100%", objectFit: "cover" }}
      ></video>
    </div>
  );
});

export default CameraStream;
